import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { getLastKnownOnlineStatus } from "../hooks/useOnlineStatus";

/**
 * Best-effort client-side downscale so uploads stay small on slow/expensive
 * connections (theme-park LTE, ship wifi). Returns a JPEG blob, or throws if
 * the browser can't decode the image (e.g. some HEIC) — callers fall back to
 * uploading the original file.
 */
async function compressImage(file: File, maxDim = 1600, quality = 0.82): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );
  if (!blob) throw new Error("toBlob failed");
  return blob;
}

/**
 * How long Storage may keep retrying one upload before giving up. The SDK
 * default is 10 minutes, and the form's Save button waits on the upload — on
 * ship wifi that is ten minutes of a frozen form. A compressed photo that
 * can't get through in a minute isn't going to; better to say so and let the
 * rest of the form save.
 */
const MAX_UPLOAD_RETRY_MS = 60_000;

/** Thrown before trying when the device is definitely offline. */
export class ImageUploadOfflineError extends Error {
  readonly code = "offline";
  constructor() {
    super("Can't upload a photo while offline.");
    this.name = "ImageUploadOfflineError";
  }
}

/** A sentence for the form to show when `uploadEntityImage` fails. */
export function imageUploadErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === "offline") {
    return "You're offline, so the photo can't upload. Save without it and add it once you're back online.";
  }
  if (code === "storage/retry-limit-exceeded") {
    return "The photo upload timed out — the connection is too weak. Save without it and add it later.";
  }
  return "Image upload failed — you can paste an image URL below instead.";
}

/**
 * Upload an entity image and return its public download URL. Compresses when
 * possible; uploads the original otherwise. Path is namespaced by uid so the
 * Storage rules can scope writes per user.
 *
 * Unlike Firestore writes, uploads don't queue offline — the bytes have to
 * reach the server before there's a URL to save. So this fails fast when we
 * know we're offline — the browser says so, or the app's reachability probe
 * does (ship wifi reports "online" with no internet behind it) — and gives up
 * after MAX_UPLOAD_RETRY_MS when the connection only looks usable.
 */
export async function uploadEntityImage(file: File, uid: string): Promise<string> {
  const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (browserOffline || getLastKnownOnlineStatus() === false) {
    throw new ImageUploadOfflineError();
  }
  let data: Blob = file;
  let ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  try {
    data = await compressImage(file);
    ext = "jpg";
  } catch {
    // Keep the original file (compression unsupported for this image).
  }
  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  // Set per call rather than in firebase.ts so the bound lives next to the
  // reason for it; it's a plain property on the shared instance.
  storage.maxUploadRetryTime = MAX_UPLOAD_RETRY_MS;
  const objectRef = ref(storage, `entity-images/${uid}/${id}.${ext}`);
  await uploadBytes(objectRef, data, { contentType: data.type || "image/jpeg" });
  return getDownloadURL(objectRef);
}
